import type { StandardSchemaV1 } from '@standard-schema/spec'
import type { z } from 'zod'

type StandardZodSchemaBase<
  TOutputSchema extends z.ZodTypeAny,
  TInputSchema extends z.ZodTypeAny | undefined,
> = StandardSchemaV1<
  TInputSchema extends z.ZodTypeAny ? z.input<TInputSchema> : z.input<TOutputSchema>,
  z.output<TOutputSchema>
> & {
  zod: TOutputSchema
} & (TInputSchema extends z.ZodTypeAny ? { input: TInputSchema } : { input?: undefined })

export type StandardZodSchema<
  TOutputSchema extends z.ZodTypeAny,
  TInputSchema extends z.ZodTypeAny | undefined = undefined,
> = StandardZodSchemaBase<TOutputSchema, TInputSchema>

export function toStandardSchema<TOutputSchema extends z.ZodTypeAny>(
  schema: TOutputSchema,
): StandardZodSchema<TOutputSchema>
export function toStandardSchema<TOutputSchema extends z.ZodTypeAny, TInputSchema extends z.ZodTypeAny>(
  schema: TOutputSchema,
  inputSchema: TInputSchema,
): StandardZodSchema<TOutputSchema, TInputSchema>
export function toStandardSchema(
  schema: z.ZodTypeAny,
  inputSchema?: z.ZodTypeAny,
): StandardZodSchema<z.ZodTypeAny, z.ZodTypeAny | undefined> {
  return {
    zod: schema,
    ...(inputSchema ? { input: inputSchema } : {}),
    '~standard': {
      version: 1,
      vendor: 'zod',
      types: {
        input: inputSchema
          ? (undefined as unknown as z.input<typeof inputSchema>)
          : (undefined as unknown as z.input<typeof schema>),
        output: undefined as unknown as z.output<typeof schema>,
      },
      validate: (value) => {
        const result = schema.safeParse(value)
        if (result.success) return { value: result.data }
        return {
          issues: result.error.issues.map((issue) => ({
            message: issue.message,
            path: issue.path.length > 0 ? issue.path : undefined,
          })),
        }
      },
    },
  }
}
